/**
 * Importação de organograma do builder do Grupo Econômico (card 3578). Espelha `OrgChartImportDto` do backend
 * (`Legatus.Vault.Common.DTOs/Business/EconomicGroups/OrgChartImportDto.cs`).
 */

/** Situação da importação (`EconomicGroupOrgChartImportStatus`, sai como texto). */
export const OrgChartImportStatuses = {
  Reading: 'Reading',
  Reconciling: 'Reconciling',
  Ready: 'Ready',
  Failed: 'Failed',
  Applied: 'Applied',
  Discarded: 'Discarded',
} as const;

export type OrgChartImportStatus =
  (typeof OrgChartImportStatuses)[keyof typeof OrgChartImportStatuses];

/** O que fazer com cada nó da proposta (`OrgChartNodeActions`). */
export const OrgChartNodeActions = {
  UseExisting: 'UseExisting',
  CreateNew: 'CreateNew',
  NeedsCnpj: 'NeedsCnpj',
  InvalidCnpj: 'InvalidCnpj',
  NotFoundInRevenue: 'NotFoundInRevenue',
  /** CNPJ já cadastrado em outro cliente do Cognito: não pode entrar, vem desmarcado. */
  RegisteredElsewhere: 'RegisteredElsewhere',
  Person: 'Person',
} as const;

export type OrgChartNodeAction = (typeof OrgChartNodeActions)[keyof typeof OrgChartNodeActions];

/** Tipo do nó no documento (`OrgChartNodeKinds`, contrato do Cog). */
export const OrgChartNodeKinds = {
  Company: 'company',
  Person: 'person',
  Other: 'other',
} as const;

export type OrgChartNodeKind = (typeof OrgChartNodeKinds)[keyof typeof OrgChartNodeKinds];

export interface OrgChartEvidenceResponse {
  /** Página (a partir de 1). */
  page: number | null;
  /** Trecho do documento (CPF sempre mascarado). */
  quote: string | null;
}

export interface OrgChartOtherGroupResponse {
  id: string;
  name: string;
}

export interface OrgChartProposalNodeResponse {
  tempId: string;
  documentName: string;
  kind: OrgChartNodeKind | string;
  /** Só dígitos e DV válido; nulo quando ausente ou inválido. */
  cnpj: string | null;
  cnpjAsWritten: string | null;
  /** Controlador principal no documento; nulo = topo. */
  parentTempId: string | null;
  /** Percentual que o pai detém neste nó (0 a 100). */
  ownershipPercentage: number | null;
  evidence: OrgChartEvidenceResponse | null;
  action: OrgChartNodeAction | string;
  existingCompanyId: string | null;
  /** Nome da empresa no cadastro do tenant, quando `action=UseExisting`. */
  existingCompanyName: string | null;
  /** Membro deste grupo que já representa a empresa. */
  existingMemberId: string | null;
  /** Razão social na Receita (é a que vale ao cadastrar). */
  revenueName: string | null;
  revenuePartners: string[];
  otherGroups: OrgChartOtherGroupResponse[];
  selected: boolean;
}

export interface OrgChartProposalResponse {
  suggestedPrimaryTempId: string | null;
  warnings: string[];
  nodes: OrgChartProposalNodeResponse[];
}

export interface OrgChartImportResponse {
  id: string;
  economicGroupId: string;
  fileName: string;
  status: OrgChartImportStatus | string;
  /** Etapa em português, pronta para a tela. */
  phase: string | null;
  /** Motivo da falha, em português, quando `status` é `Failed`. */
  errorMessage: string | null;
  /** UTC. */
  createdAt: string;
  /** UTC; nulo até a primeira mudança. */
  updatedAt: string | null;
  /** Nula até a importação ficar `Ready`. */
  proposal: OrgChartProposalResponse | null;
}
